<script lang="ts" setup>
import SettingsLayout from '@panel/layouts/Layout.User.Settings.vue';
import Section from 'gpcore-uikit/components/Component.Section.vue';
import AvatarUpload from '@panel/modules/AvatarUpload.vue';
import Size from 'gpcore-uikit/interfaces/Size';
import { computed, inject, onMounted, ref } from 'vue';
import { useSessionStore } from 'gpcore-apikit/store/pinia/session';
import { useUserStore } from 'gpcore-apikit/store/pinia/user';
import { useProjectStore } from 'gpcore-apikit/store/pinia/project';
import type Config from '@panel/config';
import { GpBtnLink, GpDropdown } from '@gportal/uikit';
import UserNodeUsage from '@panel/components/user/settings/UserNodeUsage.vue';
import useUserViewTitle from '@panel/composables/useUserViewTitle';
import useNotifications from 'gpcore-uikit/composables/useNotifications';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

useUserViewTitle('Settings');

const config = inject('config') as Config;
const userStore = useUserStore();
const projectStore = useProjectStore();
const sessionStore = useSessionStore();
const { addNotification } = useNotifications();

const session = computed(() => sessionStore.session);
const nodeLimit = computed(() => userStore.nodeLimit);
const editAccountPageURL = `${config.keycloak.url}/realms/${config.keycloak.realm}/account`;

const projectOptions = computed<Array<Record<string, string>>>(() =>
  projectStore.projects.map((project) => ({
    key: project.id,
    label: project.name,
  })),
);
const timezoneOptions = ref<Array<Record<string, string>>>([]);
const defaultProject = ref<{ key: string; label: string }>();

const setTimezone = (code: string) => {
  if (code.toString() === session.value.user.timezone || session.value.user.timezone === '') {
    return;
  }

  userStore
    .updateTimezone(code.toString())
    .then(() => {
      addNotification({
        type: 'success',
        message: 'Your timezone has been changed.',
      });

      session.value.user.timezone = code.toString();
    })
    .catch(() => {
      addNotification({
        type: 'danger',
        message: 'Failed to set the timezone.',
      });
    });
};

const isSwitchingDefaultProject = ref(false);
const setDefaultProject = (project: { key: string; label: string }) => {
  if (project == null || defaultProject.value == null) return;
  if (project.key === defaultProject.value.key) {
    return;
  }

  isSwitchingDefaultProject.value = true;
  projectStore
    .setAsDefaultProject(project.key)
    .then(() => {
      defaultProject.value = project;
      addNotification({
        type: 'success',
        message: 'Your default project has been changed.',
      });
    })
    .catch(() => {
      addNotification({
        type: 'danger',
        message: 'Failed to set the default project.',
      });
    })
    .finally(() => {
      isSwitchingDefaultProject.value = false;
    });
};

onMounted(async () => {
  await userStore.reloadNodeLimit();
  await projectStore.reloadProjects();

  userStore.listTimezones().then((response) => {
    response.timezones.forEach((timezone) => {
      timezoneOptions.value.push({ key: timezone.code, label: timezone.name });
    });

    timezoneOptions.value.sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true }),
    );
  });

  projectStore.projects.forEach((project) => {
    if (project.id === projectStore.defaultProjectId) {
      defaultProject.value = { key: project.id, label: project.name };
    }
  });
});
</script>

<template>
  <SettingsLayout :content-size="Size.Tiny" v-playwright="'user-settings-view'">
    <template #content>
      <Section :size="Size.Tiny" title="Edit user">
        <p>
          To edit your user information like username, email, password and
          two-factor-authentication, use our account management service.
        </p>
        <GpBtnLink class="tw-mt-8 tw-w-full" :href="editAccountPageURL" target="_blank">
          Open account management
          <FontAwesomeIcon
            :icon="['fas', 'fa-arrow-up-right-from-square']"
            class="tw-ml-1 tw-text-sm"
          />
        </GpBtnLink>
      </Section>

      <Section
        :size="Size.Tiny"
        v-if="session.user.timezone !== '' && timezoneOptions.length > 0"
        title="Change timezone"
      >
        <p>
          You can update your user timezone. The configured timezone affects your billing periods.
        </p>
        <GpDropdown
          class="tw-mt-4"
          :model-value="session.user.timezone"
          label="Select a timezone"
          :data="timezoneOptions"
          name="timezone"
          input-id="timezone"
          @update:model-value="($event) => setTimezone($event as string)"
        />
      </Section>

      <Section v-if="nodeLimit" :size="Size.Tiny" title="Node usage">
        <UserNodeUsage :node-limit="nodeLimit" />
      </Section>

      <Section v-if="projectOptions.length > 0" :size="Size.Tiny" title="Default project">
        <p>If you like to change your default project, please select another project below.</p>
        <GpDropdown
          class="tw-mt-4"
          :model-value="defaultProject"
          label="Select a project"
          :data="projectOptions"
          name="project"
          input-id="default_project"
          :emit-object="true"
          :disabled="isSwitchingDefaultProject"
          @update:model-value="setDefaultProject"
        />
      </Section>

      <Section :size="Size.Tiny" title="Avatar">
        <AvatarUpload :avatar-id="session.user.id" :avatar-url="session.user.avatarUrl" />
      </Section>
    </template>
  </SettingsLayout>
</template>
